import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuthor } from '@/hooks/useAuthor';
import { timeAgo } from '@/lib/format';
import type { FollowPack } from '@/lib/followPacks';
import { displayNameOf, ProfileAvatar } from '@/components/Avatar';
import { CopyNpub } from '@/components/CopyNpub';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

export function packPath(pack: Pick<FollowPack, 'pubkey' | 'id'>): string {
  return `/pack/${pack.pubkey}/${encodeURIComponent(pack.id)}`;
}

const FALLBACK_COVER_HUES: [number, number][] = [
  [84, 48],
  [200, 260],
  [20, 60],
  [280, 320],
  [150, 190],
];

function coverFallback(pack: FollowPack): string {
  let hash = 0;
  for (let i = 0; i < pack.id.length; i++) hash = (hash * 31 + pack.id.charCodeAt(i)) >>> 0;
  const [a, b] = FALLBACK_COVER_HUES[hash % FALLBACK_COVER_HUES.length];
  return `linear-gradient(120deg, hsl(${a} 75% 62%) 0%, hsl(${b} 70% 55%) 60%, hsl(${(b + 60) % 360} 65% 45%) 100%)`;
}

export function PackCard({ pack }: { pack: FollowPack }) {
  const { data } = useAuthor(pack.pubkey);
  const authorName = useMemo(
    () => displayNameOf(data?.metadata, pack.pubkey),
    [data?.metadata, pack.pubkey],
  );

  return (
    <Link
      to={packPath(pack)}
      className="group block rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring motion-safe:transition-transform motion-safe:hover:-translate-y-0.5"
    >
      <Card className="overflow-hidden rounded-2xl border shadow-sm motion-safe:transition-shadow motion-safe:group-hover:shadow-md">
        <div className="relative h-36 w-full overflow-hidden bg-muted" style={pack.coverImageUrl ? undefined : { background: coverFallback(pack) }}>
          {pack.coverImageUrl ? (
            <img
              src={pack.coverImageUrl}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <span className="absolute inset-0 grid place-items-center p-4 text-center font-display text-3xl text-white/95 drop-shadow-sm">
              {pack.name}
            </span>
          )}
          <span className="absolute bottom-2 right-2 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white">
            {pack.members.length} {pack.members.length === 1 ? 'person' : 'people'}
          </span>
        </div>
        <CardContent className="p-4">
          <h3 className="truncate text-lg font-semibold tracking-tight">{pack.name}</h3>
          <div className="mt-2 flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
            <ProfileAvatar pubkey={pack.pubkey} size="xs" />
            <span className="min-w-0">
              <span className="block truncate font-medium text-foreground">{authorName}</span>
              <CopyNpub pubkey={pack.pubkey} />
            </span>
            <span aria-hidden className="shrink-0">·</span>
            <span className="shrink-0">{timeAgo(pack.createdAt)}</span>
          </div>
          {pack.description ? (
            <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{pack.description}</p>
          ) : null}
          <AvatarStack pubkeys={pack.members.slice(0, 5).map((m) => m.pubkey)} extra={pack.members.length - 5} />
        </CardContent>
      </Card>
    </Link>
  );
}

export function AvatarStack({ pubkeys, extra }: { pubkeys: string[]; extra: number }) {
  if (pubkeys.length === 0) return null;
  return (
    <div className="mt-3 flex items-center">
      <div className="flex -space-x-2">
        {pubkeys.map((pk) => (
          <ProfileAvatar key={pk} pubkey={pk} size="sm" className="ring-2 ring-card" />
        ))}
        {extra > 0 ? (
          <span className="grid size-8 place-items-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground ring-2 ring-card">
            +{extra}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function PackCardSkeleton() {
  return (
    <Card className="overflow-hidden rounded-2xl">
      <Skeleton className="h-36 w-full rounded-none" />
      <CardContent className="space-y-2 p-4">
        <Skeleton className="h-5 w-3/4" />
        <div className="flex items-center gap-2">
          <Skeleton className="size-6 rounded-full" />
          <Skeleton className="h-4 w-1/3" />
        </div>
        <div className="flex -space-x-2 pt-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="size-8 rounded-full" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
